import { motion } from "framer-motion";
import step1Img from "@/assets/how-it-works-step1.jpg";
import step2Img from "@/assets/how-it-works-step2.jpg";
import step3Img from "@/assets/how-it-works-step3.jpg";

interface Step {
  number: number;
  label: string;
  description: string;
  image: string;
}

const steps: Step[] = [
  {
    number: 1,
    label: "Share Your Story",
    description: "Tell us about the moment,\nthe memories, and who it's for.",
    image: step1Img,
  },
  {
    number: 2,
    label: "We Create Your Song",
    description: "Our team writes and produces a custom song inspired by what you share.",
    image: step2Img,
  },
  {
    number: 3,
    label: "Listen & Share",
    description: "Receive your inspired creation and deliver a moment they'll never forget.",
    image: step3Img,
  },
];

const HowItWorksSection = () => {
  return (
    <section className="pt-4 pb-8 md:pt-6 md:pb-11 bg-card">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14 md:mb-20"
        >
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
            How Your Song Comes to Life
          </h2>
          <div className="section-divider mb-6" aria-hidden="true" />
        </motion.div>

        <div className="max-w-5xl mx-auto">
          {/* Desktop: horizontal timeline connector */}
          <div className="hidden md:block relative mb-8">
            <div className="absolute top-5 left-[16.67%] right-[16.67%] h-0.5 bg-border" />
            <div className="grid grid-cols-3">
              {steps.map((step, i) => (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{
                    delay: i * 0.25,
                    type: "spring",
                    stiffness: 400,
                    damping: 20,
                  }}
                  className="flex justify-center"
                >
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm relative z-10" aria-hidden="true">
                    {step.number}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Step cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, i) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, x: -40, y: 20 }}
                whileInView={{ opacity: 1, x: 0, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  delay: i * 0.2,
                  duration: 0.6,
                  type: "spring",
                  stiffness: 300,
                  damping: 25,
                }}
                className="text-center flex flex-col"
              >
                {/* Mobile number badge */}
                <div className="md:hidden w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm mx-auto mb-4" aria-hidden="true">
                  {step.number}
                </div>

                <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                  {step.label}
                </h3>
                <p className="text-muted-foreground text-base font-body mb-5 leading-relaxed max-w-xs mx-auto whitespace-pre-line md:min-h-[3rem]">
                  {step.description}
                </p>

                {/* Image */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.2 + 0.15, duration: 0.5 }}
                  whileHover={{ scale: 1.03 }}
                  className="aspect-[4/3] rounded-xl overflow-hidden mt-auto shadow-soft"
                >
                  <img
                    src={step.image}
                    alt={`Step ${step.number}: ${step.label} — ${step.description.replace(/\n/g, ' ')}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </motion.div>
              </motion.div>
            ))}
          </div>

          {/* Reassurance line */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-muted-foreground text-base font-body mt-10"
          >
            Crafted with care. Delivered in days. Revised until it feels right.
          </motion.p>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
